import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import { ApiError, OfflineError, apiFetch } from "@/lib/api";
import { planKeys, projectKeys, recordKeys } from "@/lib/query-keys";
import { invalidateReportContents } from "@/lib/invalidate-report-contents";
import { findPendingRecord, markPendingRecordDeleted } from "@/offline/db";

interface DeleteRecordParams {
  projectId: string;
  recordId: string;
}

/**
 * Deletes a single record.
 *
 * Branches on whether the target is still a local draft:
 * - **Pending** (an outbox entry exists for `recordId` — a pending record's
 *   id IS its `clientUuid`) → the outbox entry is **marked `deleted`** via
 *   `markPendingRecordDeleted` (NOT hard-deleted) and the `["pendingRecords"]`
 *   prefix is invalidated. A `deleted` entry is filtered out of the grid
 *   immediately (`usePendingRecords`), so the tile disappears at once, but
 *   the drain still sees it: if the create upload is in flight, the drain's
 *   `onSuccess` reads the `deleted` flag and fires a compensating server
 *   `DELETE`; if it was never uploaded, the drain purges the entry. A hard
 *   delete here would orphan a ghost record when the create lands on a
 *   lost-response retry — see [[offline-record-outbox]] § In-flight edge.
 *   No server call on this path; works offline.
 * - **Synced** → the existing server `DELETE`, unchanged. The caller gates
 *   this on connectivity — a synced record is online-only for edit/delete.
 *
 * See [[offline-record-outbox]].
 */
export function useDeleteRecord() {
  const queryClient = useQueryClient();
  const { getAccessToken } = useAuth();

  return useMutation({
    // Deleting a pending record is a purely-local IndexedDB write (the branch
    // below) and must run with no connectivity. TanStack's default mutation
    // `networkMode: "online"` would PAUSE the whole mutation while offline —
    // the `mutationFn` never runs, so confirming the delete does nothing.
    // `offlineFirst` runs the `mutationFn` once regardless of connectivity:
    // the pending branch resolves locally, and the synced branch still throws
    // a typed `OfflineError` from `apiFetch` for `onError` to surface. See
    // [[offline-record-outbox]].
    networkMode: "offlineFirst",
    // DELETE is HTTP-idempotent (RFC 9110 § 9.2.2) and the catch below
    // turns a 404-on-retry into success, so a single retry on a transient
    // failure is safe and recovers most jobsite-network blips invisibly.
    // Mirrors the queries predicate at `main.tsx` — skip `OfflineError`
    // (synchronous from apiFetch when offline; nothing to retry against)
    // and 4xx (server explicitly rejected; retry just burns backoff).
    // POSTs / non-idempotent verbs deliberately do NOT inherit this — see
    // the comment in `main.tsx` about why there is no global default.
    retry: (failureCount, err) => {
      if (err instanceof OfflineError) return false;
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        return false;
      }
      return failureCount < 1;
    },
    mutationFn: async ({ projectId, recordId }: DeleteRecordParams) => {
      // A pending record is a local draft — flag the outbox entry `deleted`
      // (no hard delete) so the drain can reconcile an in-flight upload.
      // An unreadable outbox answers "not a draft" — see `findPendingRecord`.
      const pending = await findPendingRecord(recordId);
      if (pending) {
        await markPendingRecordDeleted(recordId);
        return;
      }
      try {
        await apiFetch<void>(
          `/projects/1.2/projects/${projectId}/records/${recordId}/`,
          getAccessToken,
          { method: "DELETE" },
        );
      } catch (err) {
        // 404 = "record is already gone" → semantically successful for an
        // idempotent DELETE. A first attempt whose response was lost on a
        // flaky connection (the hook-level `retry` predicate above fires
        // a second DELETE) sees 404 from the backend and must NOT surface
        // as failure. See [[delete-record-dialog.spec]] Behavior § 6.
        if (err instanceof ApiError && err.status === 404) return;
        throw err;
      }
    },
    onSuccess: (_data, { projectId }) => {
      // Prefix-invalidate the outbox so the grid drops a deleted pending
      // tile and the always-mounted drain stops trying to upload it.
      void queryClient.invalidateQueries({
        queryKey: ["pendingRecords"],
      });
      void queryClient.invalidateQueries({
        queryKey: recordKeys.byProject(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: projectKeys.detail(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: planKeys.byProject(projectId),
      });
      // Reports holding the record show it as a tombstone, and count less.
      invalidateReportContents(queryClient, projectId, { counts: true });
    },
  });
}
